// The workflow rail: resolves the active workflow from the route, then composes the workflow
// switcher, the per-workflow nav list, the progress tracker and the account block. Nav targets the
// existing Quralyst SPA routes (Business-Research-Tool---Quralyst backend). Mobile uses an overlay
// drawer from the right. Presentation lives in WorkflowSwitcher / SidebarNavList / SidebarBottomBlock.
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useAccess } from '@/hooks/useAccess';
import { featureRolledOutTo } from '@/auth/features';
import { authService, peChangesService } from '@/services/api';
import { paths } from '@/routes/paths';
import { clearClientAuthCookies } from '@/lib/authCookies';
import { locationToPath, saveReturnTo } from '@/lib/returnTo';
import GlobalProgressTracker from '@/components/layout/GlobalProgressTracker';
import { maximizeProgress } from '@/hooks/useProgressStream';
import WorkflowSwitcher from './WorkflowSwitcher';
import SidebarNavList from './SidebarNavList';
import SidebarBottomBlock from './SidebarBottomBlock';
import {
  type ScoutVertical,
  type SidebarNavItem,
  PE_NAV,
  IB_NAV,
  RESEARCH_NAV,
  WORKFLOW_META,
  VERTICAL_STORAGE_KEY,
  detectWorkflow,
  isSharedPath,
  writeStoredRail,
} from './sidebarNav';
import './sidebar.css';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isAuthenticated, logout } = useAuth();
  const { can } = usePermissions();
  const { allows, tier } = useAccess();
  const canApprove = can('admin:approve');
  const canManageOrg = can('org:manage_members');
  const canPeDataset = can('pe:dataset');
  const canPeAdmin = can('pe:admin');
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  const workflow = detectWorkflow(location.pathname);

  // Remember the displayed rail (including landing `none`) so Deals / Settings / FAQs keep it.
  // Only workflow-owned pages write `scout_vertical` — the landing page and bottom-block
  // routes must not clobber the last chosen workflow.
  useEffect(() => {
    writeStoredRail(workflow);
    if (workflow === 'none' || isSharedPath(location.pathname)) return;
    try {
      localStorage.setItem(VERTICAL_STORAGE_KEY, workflow);
    } catch {
      /* ignore */
    }
  }, [workflow, location.pathname]);

  // Close the mobile drawer on navigation (same destination click still needs the link onClick).
  useEffect(() => {
    setMenuOpen((open) => (open ? false : open));
  }, [location.pathname]);

  // Escape + body scroll lock while the mobile menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const { data: changesSummary } = useQuery({
    queryKey: ['pe', 'changes', 'summary', 'sidebar'],
    queryFn: () => peChangesService.getChangesSummary(),
    // Activity became staff-only on both rails (§3c), so the badge it feeds is never rendered
    // for anyone else — don't spend a request on it.
    enabled: isAuthenticated && canPeAdmin && (workflow === 'pe' || workflow === 'ib'),
    staleTime: 60_000,
  });
  const changeCount = changesSummary?.total ?? 0;

  function switchWorkflow(w: ScoutVertical) {
    try {
      localStorage.setItem(VERTICAL_STORAGE_KEY, w);
    } catch {
      /* ignore */
    }
    closeMenu();
    navigate(WORKFLOW_META[w].firstPath);
  }

  const handleLogout = () => {
    closeMenu();
    // Signing back in (either method) returns here — AUTH upgrade-1.
    saveReturnTo(locationToPath(location), currentUser?.id ?? null);
    void authService.logout().catch(() => {});
    clearClientAuthCookies();
    logout();
    navigate(paths.auth.login);
  };

  const rawNav: SidebarNavItem[] =
    workflow === 'pe' ? PE_NAV : workflow === 'ib' ? IB_NAV : RESEARCH_NAV;
  const navItems =
    workflow === 'none'
      ? []
      : rawNav.filter((item) => {
          if (item.requireAdmin && !canApprove) return false;
          if (item.requirePeAdmin && !canPeAdmin) return false;
          // admin/role-based-access §2 — additive: an item declaring no `access` is unaffected,
          // and where both apply this can only hide further, never reveal.
          if (item.access && !allows(item.access)) return false;
          // §3c — entitled but not yet launched: hidden until the rollout stage opens.
          if (item.feature && !featureRolledOutTo(item.feature, tier)) return false;
          return true;
        });

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- mobile backdrop dismiss */}
      <div
        className={`sidebar-overlay${menuOpen ? ' show' : ''}`}
        id="sidebarOverlay"
        onClick={closeMenu}
      />

      <aside className={`sidebar sidebar--replit${menuOpen ? ' is-open' : ''}`} id="sidebar">
        <div className="sidebar-topbar">
          <div className="sidebar-logo">
            <a href={isAuthenticated ? paths.landing : paths.auth.login}>
              <img src="/images/quralyst-logo.svg" alt="QuraLyst" className="sidebar-logo-img" />
            </a>
          </div>

          <button
            type="button"
            className="sidebar-mobile-toggle"
            id="sidebarToggle"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={menuOpen}
            aria-controls="sidebarMenu"
          >
            <i className={`bi ${menuOpen ? 'bi-x-lg' : 'bi-list'}`} aria-hidden="true" />
          </button>
        </div>

        <div className={`sidebar-menu${menuOpen ? ' show' : ''}`} id="sidebarMenu">
          {isAuthenticated ? (
            <>
              <WorkflowSwitcher
                current={workflow}
                canPeDataset={canPeDataset}
                onSelect={switchWorkflow}
              />

              {workflow !== 'none' && (
                <SidebarNavList
                  items={navItems}
                  pathname={location.pathname}
                  ariaLabel={`${WORKFLOW_META[workflow].label} navigation`}
                  changeCount={changeCount}
                  showApprovalsBadge={false}
                  onNavigate={closeMenu}
                />
              )}

              {/* Outside <nav> so a streaming job stays visible on the landing page too. */}
              <div className="sidebar-tracker-slot">
                <GlobalProgressTracker onMaximize={maximizeProgress} />
              </div>

              <SidebarBottomBlock
                canManageOrg={canManageOrg}
                canApprove={canApprove}
                canPeAdmin={canPeAdmin}
                orgSlug={
                  currentUser?.showOrgNav && currentUser.orgNavSlug ? currentUser.orgNavSlug : null
                }
                showApprovalsBadge
                onNavigate={closeMenu}
                onLogout={handleLogout}
              />
            </>
          ) : (
            <nav className="sidebar-nav">
              <NavLink to={paths.auth.login} className="sidebar-nav-link" onClick={closeMenu}>
                <LogIn className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-nav-label">Login</span>
              </NavLink>
              <NavLink to={paths.auth.signup} className="sidebar-nav-link" onClick={closeMenu}>
                <UserPlus className="sidebar-icon" aria-hidden="true" />
                <span className="sidebar-nav-label">Sign Up</span>
              </NavLink>
            </nav>
          )}
        </div>
      </aside>
    </>
  );
}
